/**
 * useEmailCheck Hook
 *
 * Encapsulates email duplicate checking and session recovery logic
 * extracted from PartyDetails.
 *
 * Responsibilities:
 * - Check if an email is already registered (on blur)
 * - Check for existing registration session to offer recovery
 * - Manage session recovery modal state
 */

import { useState, useCallback, useRef } from 'react';
import { checkEmailExists } from '../services/c1OnboardingService';
import { resumeRegistrationSession } from '../services/registrationSessionService';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const GENERIC_DUPLICATE_MESSAGE =
  'This email is already registered with an existing organization. Please sign in with it, or use a different email.';

/**
 * A registration session is only worth offering to restore when the user actually made progress
 * — either moved past the first step, or filled in at least one section. Backend {@code /register-
 * ation/resume?email=…} can return a stub session that was created for an email at a previous
 * visit and then abandoned before any field was saved; without this filter that stub triggers a
 * "Continue Your Registration?" modal for a genuinely fresh visitor, even though there is nothing
 * to continue. Same guard also skips the modal when the response payload is missing.
 */
function hasMeaningfulProgress(session: unknown): boolean {
  if (!session || typeof session !== 'object') return false;
  const s = session as {
    currentStep?: string;
    businessDetails?: Record<string, unknown>;
    reviewSign?: Record<string, unknown>;
    planSelection?: Record<string, unknown>;
    paymentDetails?: Record<string, unknown>;
  };
  if (s.currentStep && s.currentStep !== 'party-details') return true;
  if (s.businessDetails && Object.keys(s.businessDetails).length > 0) return true;
  if (s.reviewSign && Object.keys(s.reviewSign).length > 0) return true;
  if (s.planSelection && Object.keys(s.planSelection).length > 0) return true;
  if (s.paymentDetails && Object.keys(s.paymentDetails).length > 0) return true;
  return false;
}

export interface SessionData {
  currentStep: string;
  email?: string;
  businessDetails?: Record<string, unknown>;
  reviewSign?: Record<string, unknown>;
  planSelection?: Record<string, unknown>;
  paymentDetails?: Record<string, unknown>;
}

export interface UseEmailCheckOptions {
  /** The current email in the parent form's saved data (to skip re-checking) */
  savedEmail?: string;
  /** Callback when a previous session is found and user wants to restore */
  onSessionFound?: (session: SessionData) => void;
}

export interface UseEmailCheckReturn {
  /** Whether the email duplicate check is in progress */
  isCheckingEmail: boolean;
  /** Whether the session recovery check is in progress */
  isCheckingSession: boolean;
  /** Error message from email check (e.g. "already registered") */
  emailError: string;
  /** Whether to show the session recovery modal */
  showSessionModal: boolean;
  /** The found session data (if any) */
  foundSession: SessionData | null;
  /** Run the full email check (duplicate + session recovery). Call on blur. */
  checkEmail: (email: string) => Promise<void>;
  /**
   * Duplicate-only check — hits {@code /onboarding/check-email} and updates {@link emailError}
   * without probing session recovery. Intended for debounced-while-typing callers so the
   * recovery modal doesn't flash on every keystroke.
   */
  checkDuplicate: (email: string) => Promise<void>;
  /** User chose to restore the found session */
  handleRestoreSession: () => void;
  /** User chose to start fresh (dismiss modal) */
  handleStartFresh: () => void;
  /** Clear any email error (e.g. when user edits the field) */
  clearError: () => void;
}

export function useEmailCheck(options: UseEmailCheckOptions = {}): UseEmailCheckReturn {
  const { savedEmail, onSessionFound } = options;

  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [foundSession, setFoundSession] = useState<SessionData | null>(null);

  // Track the latest normalized email a caller cared about so an older debounced check that
  // resolves after the user has moved on cannot clobber the newer result.
  const lastRequestedEmailRef = useRef<string>('');

  const runDuplicateCheck = useCallback(async (email: string, requestId: string): Promise<boolean> => {
    setIsCheckingEmail(true);
    try {
      const result = await checkEmailExists(email);
      // Discard a stale response — the user has since typed a different email.
      if (lastRequestedEmailRef.current !== requestId) return false;
      if (result.exists) {
        setEmailError(result.message || GENERIC_DUPLICATE_MESSAGE);
        return true;
      }
      setEmailError('');
      return false;
    } catch {
      // Non-blocking: swallow and treat as "unknown"; the on-submit and server-side checks
      // remain as safety nets.
      return false;
    } finally {
      if (lastRequestedEmailRef.current === requestId) {
        setIsCheckingEmail(false);
      }
    }
  }, []);

  const checkDuplicate = useCallback(async (email: string) => {
    const normalized = email.trim().toLowerCase();
    if (!normalized || !EMAIL_REGEX.test(normalized)) return;
    lastRequestedEmailRef.current = normalized;
    await runDuplicateCheck(normalized, normalized);
  }, [runDuplicateCheck]);

  const checkEmail = useCallback(async (email: string) => {
    if (!email || !EMAIL_REGEX.test(email)) return;
    const normalized = email.trim().toLowerCase();
    lastRequestedEmailRef.current = normalized;

    // 1. Duplicate check — same endpoint used by the debounced-typing and submit-time paths.
    const registered = await runDuplicateCheck(normalized, normalized);
    if (registered) return; // Do not offer session recovery for a duplicate.

    // 2. Session recovery check
    if (showSessionModal) return;
    if (!onSessionFound) return;
    if (savedEmail === email) return;
    if (foundSession?.email === email.toLowerCase().trim()) return;

    setIsCheckingSession(true);
    try {
      const response = await resumeRegistrationSession(email);
      if (response.found && response.session && hasMeaningfulProgress(response.session)) {
        setFoundSession(response.session as unknown as SessionData);
        setShowSessionModal(true);
      }
    } catch {
      // Non-blocking
    } finally {
      setIsCheckingSession(false);
    }
  }, [savedEmail, onSessionFound, showSessionModal, foundSession?.email]);

  const handleRestoreSession = useCallback(() => {
    if (foundSession && onSessionFound) {
      onSessionFound(foundSession);
    }
    setShowSessionModal(false);
    setFoundSession(null);
  }, [foundSession, onSessionFound]);

  const handleStartFresh = useCallback(() => {
    setShowSessionModal(false);
    setFoundSession(null);
  }, []);

  const clearError = useCallback(() => {
    setEmailError('');
  }, []);

  return {
    isCheckingEmail,
    isCheckingSession,
    emailError,
    showSessionModal,
    foundSession,
    checkEmail,
    checkDuplicate,
    handleRestoreSession,
    handleStartFresh,
    clearError,
  };
}
